import React, { useState } from 'react';

import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';

import PropertyMap from '~/components/Common/PropertyMap';

import type { OfferBuilderProperty } from './types';

interface Props {
  property?: OfferBuilderProperty;
}

export default function LocationMapSection({ property }: Props) {
  const [mapKey, setMapKey] = useState(0);
  const latitude = property?.latitude;
  const longitude = property?.longitude;
  const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number';
  const mapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
    : undefined;
  const locationMapSubtitle = hasCoordinates
    ? `Lat ${latitude} · Lng ${longitude} · Use Map / Satellite controls inside the frame · pin shows property coordinates`
    : 'Property coordinates shown on map';

  return (
    <Paper variant="outlined" sx={{ mt: 3, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ p: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5">Location map</Typography>
          <Typography variant="body2" color="text.secondary">
            {locationMapSubtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<MyLocationIcon />}
            disabled={!hasCoordinates}
            onClick={() => setMapKey((currentKey) => currentKey + 1)}
            sx={{ minWidth: 'max-content', px: 1, whiteSpace: 'nowrap' }}
          >
            Re-center
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon />}
            disabled={!mapsUrl}
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 'max-content', px: 1, whiteSpace: 'nowrap' }}
          >
            Open in Maps
          </Button>
        </Stack>
      </Stack>
      <Divider />
      {hasCoordinates ? (
        <PropertyMap key={mapKey} lat={latitude} lng={longitude} />
      ) : (
        <Box sx={{ height: 360, display: 'grid', placeItems: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            No property coordinates available.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
